/** The working tool's parts (DESIGN §16.6): every page is built from these, the Plan's look included. */
import { type KeyboardEvent, type MouseEvent, type ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate } from "react-router";
import { Chip as PlanChip } from "../builder/components/Chip.js";

export { WeekArrows, type WeekNav } from "./WeekNav.js";

// MARK: Page header

/** Title, a context line, actions on the right; `lead` (the week's ‹ ›) sits before the title. */
export function PageHeader({ title, context, actions, lead }: { title: ReactNode; context?: ReactNode; actions?: ReactNode; lead?: ReactNode }) {
  return (
    <header className="k-header">
      {lead ? <div className="k-header-lead">{lead}</div> : null}
      <div className="k-header-title">
        <h1>{title}</h1>
        {context ? <span className="k-header-context">{context}</span> : null}
      </div>
      {actions ? <div className="k-header-actions">{actions}</div> : null}
    </header>
  );
}

// MARK: Table

export interface Column<T> {
  key: string;
  label: string;
  render: (row: T) => ReactNode;
  /** Sortable when given; ascending order. */
  sort?: (a: T, b: T) => number;
  align?: "start" | "end";
  /** Stays in view when the table scrolls sideways. */
  sticky?: boolean;
  /** Text may wrap in this column. */
  wrap?: boolean;
  /** Visually hidden header (the column still has a name for VoiceOver). */
  hideLabel?: boolean;
  className?: string;
}

export interface Sort {
  key: string;
  dir: "asc" | "desc";
}

/**
 * A table whose rows open something: a click, or Enter on a focused row; ↑/↓ move between rows.
 * The first column's content is a real link when `href` is given, for the keyboard and VoiceOver.
 */
export function Table<T>({
  rows,
  columns,
  rowKey,
  href,
  selected,
  label,
  initialSort,
  rowClass,
  empty,
}: {
  rows: T[];
  columns: Column<T>[];
  rowKey: (row: T) => string;
  href?: (row: T) => string;
  selected?: string | null;
  label: string;
  initialSort?: Sort;
  rowClass?: (row: T) => string | undefined;
  empty?: ReactNode;
}) {
  const navigate = useNavigate();
  const [sort, setSort] = useState<Sort | null>(initialSort ?? null);
  const body = useRef<HTMLTableSectionElement>(null);
  const sorted = useMemo(() => {
    const col = sort ? columns.find((c) => c.key === sort.key) : undefined;
    if (!col?.sort) return rows;
    const out = [...rows].sort(col.sort);
    return sort!.dir === "desc" ? out.reverse() : out;
  }, [rows, columns, sort]);

  const toggle = (key: string) =>
    setSort((s) => (s?.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: key === columns[0]?.key ? "asc" : "desc" }));

  // One path for a click anywhere in the row, the name's link included: the row navigates; a modified click is the browser's.
  const openRow = (e: MouseEvent<HTMLTableRowElement>, to: string | undefined) => {
    if (!to || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    const hit = (e.target as Element).closest(INTERACTIVE);
    if (hit && !hit.classList.contains("k-row-link")) return;
    e.preventDefault();
    navigate(to);
  };

  const onKey = (e: KeyboardEvent<HTMLTableRowElement>, row: T) => {
    if (e.key === "Enter" && href && e.target === e.currentTarget) {
      e.preventDefault();
      navigate(href(row));
    } else if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      const all = Array.from(body.current?.querySelectorAll<HTMLTableRowElement>("tr.k-row") ?? []);
      const i = all.indexOf(e.currentTarget);
      all[Math.max(0, Math.min(all.length - 1, i + (e.key === "ArrowDown" ? 1 : -1)))]?.focus();
    }
  };

  return (
    <div className="k-table-wrap">
      <table className="k-table" aria-label={label}>
        <thead>
          <tr>
            {columns.map((c) => {
              const active = sort?.key === c.key;
              const cls = [c.align === "end" ? "k-end" : "", c.sticky ? "k-sticky" : "", c.className ?? ""].filter(Boolean).join(" ") || undefined;
              return (
                <th key={c.key} scope="col" className={cls} aria-sort={active ? (sort!.dir === "asc" ? "ascending" : "descending") : c.sort ? "none" : undefined}>
                  {c.sort ? (
                    <button type="button" className="k-sort" onClick={() => toggle(c.key)}>
                      <span className={c.hideLabel ? "sr" : undefined}>{c.label}</span>
                      {active ? (
                        <span className="k-sort-mark" aria-hidden="true">
                          {sort!.dir === "asc" ? "▲" : "▼"}
                        </span>
                      ) : null}
                    </button>
                  ) : (
                    <span className={c.hideLabel ? "sr" : undefined}>{c.label}</span>
                  )}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody ref={body}>
          {sorted.length === 0 ? (
            <tr>
              <td className="k-empty" colSpan={columns.length}>
                {empty ?? "Nothing here yet."}
              </td>
            </tr>
          ) : (
            sorted.map((row) => {
              const key = rowKey(row);
              const to = href?.(row);
              const cls = ["k-row-base", to ? "k-row" : "", selected === key ? "is-selected" : "", rowClass?.(row) ?? ""].filter(Boolean).join(" ");
              return (
                <tr
                  key={key}
                  className={cls}
                  tabIndex={to ? 0 : undefined}
                  aria-selected={selected != null ? selected === key : undefined}
                  onClick={(e) => openRow(e, to)}
                  onKeyDown={(e) => onKey(e, row)}
                >
                  {columns.map((c, i) => {
                    const cls = [c.align === "end" ? "k-end" : "", c.sticky ? "k-sticky" : "", c.wrap ? "k-wrap" : "", c.className ?? ""].filter(Boolean).join(" ") || undefined;
                    const content = c.render(row);
                    return i === 0 ? (
                      <th key={c.key} scope="row" className={cls}>
                        {to ? (
                          <Link className="k-row-link" to={to} tabIndex={-1} onClick={leaveToRow}>
                            {content}
                          </Link>
                        ) : (
                          content
                        )}
                      </th>
                    ) : (
                      <td key={c.key} className={cls}>
                        {content}
                      </td>
                    );
                  })}
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
}

const INTERACTIVE = "a[href], button, select, input, textarea, summary, label, [role=button], [role=checkbox], [role=switch]";

/** The row's own link leaves a plain click to the row, so the two never both navigate or both bail out. */
function leaveToRow(e: MouseEvent<HTMLAnchorElement>) {
  if (e.button === 0 && !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey) e.preventDefault();
}

/** Ascending comparisons for `Column.sort`. */
export const byText = <T,>(f: (row: T) => string) => (a: T, b: T) => f(a).localeCompare(f(b), undefined, { sensitivity: "base", numeric: true });
export const byNumber = <T,>(f: (row: T) => number) => (a: T, b: T) => f(a) - f(b);

// MARK: Side panel

/**
 * Details beside the table (DESIGN §16.6): its own address, Esc or ✕ closes back to `closeTo`.
 * `wide` for an athlete, whose page has charts.
 */
export function SidePanel({ title, subtitle, closeTo, wide, actions, children }: { title: ReactNode; subtitle?: ReactNode; closeTo: string; wide?: boolean; actions?: ReactNode; children: ReactNode }) {
  const navigate = useNavigate();
  const ref = useRef<HTMLElement>(null);
  useEffect(() => {
    ref.current?.focus({ preventScroll: true });
  }, [closeTo]);
  useEffect(() => {
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key !== "Escape" || e.defaultPrevented) return;
      if ((e.target as Element | null)?.closest?.("input, textarea, select, [role=dialog], [role=menu]")) return;
      navigate(closeTo);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [closeTo, navigate]);
  return createPortal(
    <aside className={"k-panel" + (wide ? " is-wide" : "")} ref={ref} tabIndex={-1} aria-label={typeof title === "string" ? title : undefined}>
      <div className="k-panel-head">
        <div className="k-panel-title">
          <h2>{title}</h2>
          {subtitle ? <span className="k-panel-sub">{subtitle}</span> : null}
        </div>
        {actions}
        <Link className="k-close" to={closeTo} aria-label="Close" title="Close (Esc)">
          <svg viewBox="0 0 14 14" aria-hidden="true">
            <path d="m2 2 10 10M12 2 2 12" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
          </svg>
        </Link>
      </div>
      <div className="k-panel-body">{children}</div>
    </aside>,
    document.getElementById("view") ?? document.body,
  );
}

// MARK: Marks

export type StatusKind = "done" | "skipped" | "missed" | "today" | "planned" | "logged" | "rest" | "high" | "low" | "usual";

const STATUS_WORD: Record<StatusKind, string> = {
  done: "Done",
  skipped: "Skipped",
  missed: "Not logged",
  today: "Today",
  planned: "Planned",
  logged: "Logged",
  rest: "Rest",
  high: "High",
  low: "Low",
  usual: "Usual",
};

const STATUS_ICON: Partial<Record<StatusKind, ReactNode>> = {
  done: <path d="M2.5 6.5 5 9l4.5-5.5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />,
  missed: <path d="M3 6h6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />,
  skipped: (
    <>
      <circle cx="6" cy="6" r="4.75" fill="none" stroke="currentColor" strokeWidth="1.3" />
      <path d="M3.75 6h4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </>
  ),
  today: <circle cx="6" cy="6" r="3.5" fill="currentColor" />,
  planned: <circle cx="6" cy="6" r="3.5" fill="none" stroke="currentColor" strokeWidth="1.5" />,
  logged: <path d="M2.5 6.5 5 9l4.5-5.5" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />,
  high: <path d="M6 10V2.5M3 5.5 6 2.5l3 3" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />,
  low: <path d="M6 2v7.5M3 6.5 6 9.5l3-3" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />,
};

/** A state as a symbol and a word, never colour alone. `text` replaces the word. */
export function Status({ kind, text, hideWord }: { kind: StatusKind; text?: string; hideWord?: boolean }) {
  const icon = STATUS_ICON[kind];
  const word = text ?? STATUS_WORD[kind];
  return (
    <span className={"k-status is-" + kind} title={hideWord ? word : undefined}>
      {icon ? (
        <svg viewBox="0 0 12 12" aria-hidden="true">
          {icon}
        </svg>
      ) : null}
      <span className={hideWord ? "sr" : undefined}>{word}</span>
    </span>
  );
}

/** Personal bests: a trophy and the count, the words for VoiceOver. */
export function PbMark({ n }: { n: number }) {
  if (!n) return null;
  return (
    <span className="k-pb" title={n === 1 ? "1 personal best" : `${n} personal bests`}>
      <svg viewBox="0 0 12 12" aria-hidden="true">
        <path fill="currentColor" d="M3 1.5h6v1.2h1.8v1.1c0 1.3-.9 2.3-2.1 2.5A3 3 0 0 1 6.6 8v1.3H8v1.2H4V9.3h1.4V8a3 3 0 0 1-2.1-1.7C2.1 6.1 1.2 5.1 1.2 3.8V2.7H3V1.5Zm-.6 2.3c0 .6.3 1 .7 1.2V3.8h-.7Zm7.2 0h-.7V5c.4-.2.7-.6.7-1.2Z" />
      </svg>
      <span aria-hidden="true">{n}</span>
      <span className="sr">{n === 1 ? "1 personal best" : `${n} personal bests`}</span>
    </span>
  );
}

/** The Plan's session chip: the family's tint, the label as text. */
export function Chip({ label }: { label: string }) {
  return <PlanChip label={label} />;
}

/** A small line of values, the last one marked; decorative (say the numbers in text beside it). */
export function Sparkline({ values, width = 72, height = 20, bars }: { values: number[]; width?: number; height?: number; bars?: boolean }) {
  if (!values.length) return null;
  const max = Math.max(...values, 1);
  const step = values.length > 1 ? width / (values.length - 1) : 0;
  const y = (v: number) => height - 2 - (v / max) * (height - 4);
  if (bars) {
    const w = width / values.length;
    return (
      <svg className="k-spark" width={width} height={height} viewBox={`0 0 ${width} ${height}`} aria-hidden="true" focusable="false">
        {values.map((v, i) => (
          <rect key={i} className={"k-spark-bar" + (i === values.length - 1 ? " is-last" : "")} x={i * w + 1} y={y(v)} width={Math.max(1, w - 2)} height={Math.max(v > 0 ? 1.5 : 0, height - 2 - y(v))} rx={1} />
        ))}
      </svg>
    );
  }
  const d = values.map((v, i) => `${i ? "L" : "M"}${(i * step).toFixed(1)},${y(v).toFixed(1)}`).join(" ");
  return (
    <svg className="k-spark" width={width} height={height} viewBox={`0 0 ${width} ${height}`} aria-hidden="true" focusable="false">
      <path d={d} />
      <circle cx={(values.length - 1) * step} cy={y(values[values.length - 1]!)} r={2.5} />
    </svg>
  );
}
